import type { Tenant } from '@mg/shared';
import { useToast } from '../../../components/ui/toast';
import { WorkspaceApprovalsSection } from '../../../components/workspace/WorkspaceApprovalsSection';
import { WorkspaceMediaSection } from '../../../components/workspace/WorkspaceMediaSection';
import { WorkspaceTemplatesSection } from '../../../components/workspace/WorkspaceTemplatesSection';
import { BRAND_TOASTS } from '../../brand/brandCopy';
import { APPROVALS_ANCHOR } from '../../testMode/testMode';
import { EditSection } from '../EditSection';
import { PageHeadingCard } from '../PageHeadingCard';

export const WORKFLOW_PAGE_HEADING = {
  title: 'Workflow',
  description:
    "How your brand's content is reviewed and approved, how many images it may generate, and the design templates it uses.",
} as const;

/**
 * The Workflow page (spec:167; web.md §6.4): the heading card, then Approvals, Approval role
 * assignment, Image generation and Design templates, with today's fields and copy. The first three save
 * through the page's sticky footer, in the order E39 needs: the approval settings (order 10), then the
 * image limit (20), then the approver matrix (30). Template actions save at once, as they always have,
 * and now confirm with the "Changes saved" toast (O4). `#approvals` (the test-mode link) lands on the
 * Approvals card.
 */
export function WorkflowPage({ workspace, canEdit }: { workspace: Tenant; canEdit: boolean }) {
  const toast = useToast();
  return (
    <>
      <PageHeadingCard title={WORKFLOW_PAGE_HEADING.title} description={WORKFLOW_PAGE_HEADING.description} />
      <WorkspaceApprovalsSection
        workspace={workspace}
        canEdit={canEdit}
        approvalsId={APPROVALS_ANCHOR}
        rolesId="approval-role-assignment"
      />
      <EditSection page="workflow" id="image-generation">
        <WorkspaceMediaSection workspace={workspace} canEdit={canEdit} />
      </EditSection>
      <EditSection page="workflow" id="design-templates">
        <WorkspaceTemplatesSection
          workspace={workspace}
          canEdit={canEdit}
          onSaved={() => toast.show(BRAND_TOASTS.changesSaved)}
        />
      </EditSection>
    </>
  );
}
